import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useTimezoneStore, resolveTimezone } from "@/shared/stores/useTimezoneStore.ts";
import { activeLocale, dayjsLocale } from "@/shared/lib/i18n/formatters.ts";

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Location-local time display.
 *
 * Dates in the app are true UTC instants (see convert-datetime). They were all
 * rendered in the VIEWER's browser zone, so a site in a different region showed
 * shifted times. We render in the active LOCATION's IANA zone instead, which is
 * DST-correct (a fixed hour offset would be wrong half the year).
 *
 * The zone comes from the location's configured `timezone` served by
 * `/api/settings/` — the single source of truth — resolved through
 * {@link useTimezoneStore}. A location with no configured zone falls back to the
 * viewer's own zone. There is deliberately no organization-level timezone: the
 * backend models timezone per location only.
 *
 * NOTE: request params (format-datetime) and parsing (convert-datetime) stay
 * UTC — only DISPLAY is location-local.
 *
 * The zone and the LANGUAGE are separate axes: the zone decides which instant is
 * shown, the language only how it is spelled. Nothing below may let the active
 * language reach zone resolution — `org-time.test.ts` pins that.
 *
 * NAMING: the `*OrgTime*` names predate per-location zones. They are kept as-is
 * to avoid churning their call sites; the source is the location.
 */

/**
 * The active location's timezone, read NON-reactively — for pure formatters and
 * code outside React. Components that must re-render when the active location or
 * its zone changes should use {@link useOrgTimeZone} instead.
 */
export const currentOrgTimeZone = (): string => resolveTimezone(useTimezoneStore.getState());

/**
 * The active location's IANA timezone; re-renders the caller when the active
 * location changes or its configured zone is updated.
 */
export const useOrgTimeZone = (): string => useTimezoneStore(resolveTimezone);

/**
 * Format a UTC instant in the active location's timezone. `pattern` is a dayjs
 * format string. Pass an explicit `tz` from {@link useOrgTimeZone} in React to
 * stay reactive; it defaults to the resolved zone read from the store.
 */
export const formatOrgTime = (
  value: Date | number | string,
  pattern = "MMM D, HH:mm:ss",
  tz: string = currentOrgTimeZone(),
): string => dayjs(value).tz(tz).locale(dayjsLocale()).format(pattern);

/**
 * Formatters, cached per (locale, zone, options).
 *
 * Building an `Intl.DateTimeFormat` costs far more than using one, and
 * `toLocaleTimeString` builds a fresh one on every call. The timeline axis
 * formats one label per tick on every zoom step — profiling a range-mode zoom
 * measured 1960 ms of a 4.5 s interaction inside this one function, which is
 * what made zooming lag by ~380 ms. Reusing the formatter removes the
 * construction from every call after the first of that shape.
 */
const formatterCache = new Map<string, Intl.DateTimeFormat>();

/**
 * `toLocaleTimeString` fills in hour/minute/second when the caller names no time
 * field; a bare `Intl.DateTimeFormat` would print a DATE instead. Replicating
 * that defaulting is what keeps the cached path printing exactly what the direct
 * call printed.
 */
const namesATimeField = (options: Intl.DateTimeFormatOptions): boolean =>
  options.dayPeriod !== undefined ||
  options.hour !== undefined ||
  options.minute !== undefined ||
  options.second !== undefined ||
  // `fractionalSecondDigits` is deliberately absent: this TS lib's
  // `DateTimeFormatOptions` does not declare it, so no caller can pass it.
  options.timeStyle !== undefined;

const timeFormatter = (tz: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat => {
  const resolved: Intl.DateTimeFormatOptions = namesATimeField(options)
    ? { ...options, timeZone: tz }
    : { ...options, hour: "numeric", minute: "numeric", second: "numeric", timeZone: tz };

  // Key over sorted entries so two call sites spelling the same options in a
  // different order share one formatter. The locale leads the key: switching
  // language must not hand back the previous language's formatter.
  const locale = activeLocale();
  const entries = Object.entries(resolved as Record<string, unknown>).sort(([a], [b]) =>
    a < b ? -1 : 1,
  );
  let key = `locale=${locale};`;
  for (const [k, v] of entries) key += `${k}=${String(v)};`;

  const cached = formatterCache.get(key);
  if (cached) return cached;
  const created = new Intl.DateTimeFormat(locale, resolved);
  formatterCache.set(key, created);
  return created;
};

/**
 * `Date#toLocaleTimeString(options)` pinned to the active location's timezone — a drop-in
 * for existing `toLocaleTimeString` calls that keeps their exact options and
 * just injects `timeZone`.
 */
export const formatOrgLocaleTime = (
  value: Date | number | string,
  options: Intl.DateTimeFormatOptions = {},
  tz: string = currentOrgTimeZone(),
): string => timeFormatter(tz, options).format(new Date(value));
